/**
 * juliette-session — Helpers de cycle de vie des sessions Juliette V3.5.
 *
 * getOrCreateReactiviteSession : reprend la conversation "reactivite" ouverte la
 * plus recente (< 6h) au lieu d'en creer une vierge a chaque ouverture de l'orb.
 * Evite a Juliette de "perdre le fil" entre deux ouvertures rapprochees, tout en
 * repartant proprement sur une nouvelle session apres une longue pause.
 */

import { supabase } from "@/integrations/supabase/client";

/** Au-dela de ce delai, on considere que c'est une nouvelle conversation. */
const RESUME_WINDOW_MS = 6 * 60 * 60 * 1000; // 6h

/**
 * Reprend la derniere session "reactivite" encore ouverte et recente, sinon en
 * cree une nouvelle. Retourne l'id de session a utiliser.
 *
 * @param teamId  team de l'agent (resolu en amont)
 * @param entry   point d'entree pour la metadata ("fullscreen_orb" | "desktop_panel")
 */
export async function getOrCreateReactiviteSession(
  teamId: string,
  entry: string,
): Promise<string> {
  const sinceIso = new Date(Date.now() - RESUME_WINDOW_MS).toISOString();

  // 1) Tente de reprendre une session reactivite ouverte recente.
  const { data: existing } = await supabase
    .from("juliette_sessions")
    .select("id")
    .eq("team_id", teamId)
    .eq("session_type", "reactivite")
    .eq("status", "open")
    .is("ended_at", null)
    .gte("started_at", sinceIso)
    .order("started_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (existing && (existing as { id: string }).id) {
    return (existing as { id: string }).id;
  }

  // 2) Sinon, nouvelle session.
  const { data, error } = await supabase
    .from("juliette_sessions")
    .insert({
      team_id: teamId,
      session_type: "reactivite",
      status: "open",
      metadata: { entry },
    })
    .select("id")
    .single();
  if (error) throw new Error(error.message);
  return (data as { id: string }).id;
}

// ─── Threads libres (conversations facon ChatGPT) ──────────────────────────

/** Resume d'un thread pour la liste d'historique. */
export interface JulietteThreadSummary {
  id: string;
  title: string | null;
  sessionType: string;
  status: string;
  startedAt: string;
  totalTurns: number;
  summary: string | null;
  /** 1er message de l'agent : sert de libelle distinctif quand il n'y a pas de titre. */
  firstUserMessage: string | null;
}

/** Libelle lisible d'un type de session pour l'historique. */
export function sessionTypeLabel(type: string): string {
  switch (type) {
    case "debrief_soir":
      return "Débrief du soir";
    case "briefing_matin":
      return "Briefing du matin";
    case "reactivite":
      return "Échange rapide";
    case "chat":
      return "Conversation";
    default:
      return "Session";
  }
}

/**
 * Cree une nouvelle conversation libre (thread). Retourne l'id de session.
 * Le titre est pose plus tard a partir du premier message (maybeAutoTitleThread).
 */
export async function createChatThread(teamId: string, entry: string): Promise<string> {
  const { data, error } = await supabase
    .from("juliette_sessions")
    .insert({
      team_id: teamId,
      session_type: "chat",
      status: "open",
      metadata: { entry },
    })
    .select("id")
    .single();
  if (error) throw new Error(error.message);
  return (data as { id: string }).id;
}

/**
 * Liste les conversations passees de la team (threads + sessions auto qui ont
 * vraiment eu lieu), les plus recentes d'abord. Sert au tiroir d'historique.
 */
export async function listJulietteThreads(
  teamId: string,
  limit = 40,
): Promise<JulietteThreadSummary[]> {
  const { data, error } = await supabase
    .from("juliette_sessions")
    .select("id, title, session_type, status, started_at, total_turns, session_summary")
    .eq("team_id", teamId)
    .or("title.not.is.null,total_turns.gt.0,session_summary.not.is.null")
    .order("started_at", { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);

  type Row = {
    id: string;
    title: string | null;
    session_type: string;
    status: string;
    started_at: string;
    total_turns: number | null;
    session_summary: string | null;
  };
  const rows = (data ?? []) as Row[];

  // 1er message "user" de chaque session -> libelle distinctif quand pas de titre.
  const firstByseesion = new Map<string, string>();
  if (rows.length > 0) {
    const { data: msgs } = await supabase
      .from("juliette_messages")
      .select("session_id, content, role, created_at")
      .in("session_id", rows.map((r) => r.id))
      .eq("role", "user")
      .order("created_at", { ascending: true });
    type Msg = { session_id: string; content: string | null };
    for (const m of (msgs ?? []) as Msg[]) {
      const c = (m.content ?? "").trim();
      if (!c || c.startsWith("[ACTION:")) continue;
      if (!firstByseesion.has(m.session_id)) {
        firstByseesion.set(m.session_id, c.replace(/\s+/g, " ").slice(0, 60));
      }
    }
  }

  return rows.map((r) => ({
    id: r.id,
    title: r.title,
    sessionType: r.session_type,
    status: r.status,
    startedAt: r.started_at,
    totalTurns: r.total_turns ?? 0,
    summary: r.session_summary,
    firstUserMessage: firstByseesion.get(r.id) ?? null,
  }));
}

// ─── Onglets de conversation (un fil par client, facon Chrome) ─────────────

/** Un onglet de la barre : un fil de conversation, attache a un contact ou General. */
export interface JulietteThreadTab {
  sessionId: string;
  /** null = fil "General" (aucun contact attache). */
  contactId: string | null;
  label: string;
  lastTurnAt: string | null;
}

/**
 * Onglets ouverts : les fils recents (chat/reactivite) dedupliques par contact
 * (le plus recent gagne, tri LRU sur metadata.last_turn_at), hors fils fermes a
 * la main (metadata.tab_closed). Le fil General (contact null) est deduplique
 * pareil — il alimente la "maison" epinglee de la barre.
 */
export async function listOpenTabs(teamId: string, limit = 5): Promise<JulietteThreadTab[]> {
  const { data, error } = await supabase
    .from("juliette_sessions")
    .select("id, title, started_at, metadata")
    .eq("team_id", teamId)
    .eq("status", "open")
    .in("session_type", ["chat", "reactivite"])
    .order("started_at", { ascending: false })
    .limit(30);
  if (error) throw new Error(error.message);

  type Row = {
    id: string;
    title: string | null;
    started_at: string;
    metadata: Record<string, unknown> | null;
  };
  const candidates = ((data ?? []) as Row[])
    .map((r) => {
      const meta = (r.metadata ?? {}) as Record<string, unknown>;
      const contactId =
        typeof meta.thread_contact_id === "string" && meta.thread_contact_id.length > 0
          ? meta.thread_contact_id
          : null;
      const focusName =
        typeof meta.focused_contact_name === "string" ? meta.focused_contact_name : "";
      const lastTurnAt = typeof meta.last_turn_at === "string" ? meta.last_turn_at : null;
      return {
        sessionId: r.id,
        contactId,
        label: contactId ? focusName || r.title || "Contact" : "Général",
        lastTurnAt,
        closed: meta.tab_closed === true,
        // Tri LRU : dernier tour, sinon date de creation (fils d'avant le stamp).
        sortKey: lastTurnAt ?? r.started_at,
      };
    })
    .sort((a, b) => b.sortKey.localeCompare(a.sortKey));

  const tabs: JulietteThreadTab[] = [];
  const seen = new Set<string>();
  for (const c of candidates) {
    if (c.closed) continue;
    const key = c.contactId ?? "__general__";
    if (seen.has(key)) continue;
    seen.add(key);
    tabs.push({
      sessionId: c.sessionId,
      contactId: c.contactId,
      label: c.label,
      lastTurnAt: c.lastTurnAt,
    });
    if (tabs.length >= limit) break;
  }
  return tabs;
}

/** Ligne minimale d'une session candidate a la reutilisation (fil vierge). */
export interface FreshThreadCandidate {
  id: string;
  total_turns: number | null;
  metadata: Record<string, unknown> | null;
}

/**
 * Vrai si ce fil est un General VIERGE reutilisable : zero tour joue, pas de
 * contact attache, pas ferme a la main. Sert de garde anti-doublons au bouton
 * "+" : cliquer plusieurs fois sans ecrire ne cree pas des coquilles vides.
 * Pure et exportee pour le test.
 */
export function isReusableFreshThread(row: FreshThreadCandidate): boolean {
  if ((row.total_turns ?? 0) > 0) return false;
  const meta = (row.metadata ?? {}) as Record<string, unknown>;
  const hasContact =
    typeof meta.thread_contact_id === "string" && meta.thread_contact_id.length > 0;
  return !hasContact && meta.tab_closed !== true;
}

/**
 * "+ Nouvelle conversation" : reprend un fil General vierge existant s'il y en
 * a un (garde anti-doublons), sinon cree un thread neuf. Retourne l'id.
 */
export async function getOrCreateFreshChatThread(
  teamId: string,
  entry: string,
): Promise<string> {
  const { data } = await supabase
    .from("juliette_sessions")
    .select("id, total_turns, metadata")
    .eq("team_id", teamId)
    .eq("status", "open")
    .in("session_type", ["chat", "reactivite"])
    .order("started_at", { ascending: false })
    .limit(10);
  const fresh = ((data ?? []) as FreshThreadCandidate[]).find(isReusableFreshThread);
  if (fresh) return fresh.id;
  return createChatThread(teamId, entry);
}

/** Ferme un onglet (le fil reste dans l'Historique ; un nouveau tour le rouvre). */
export async function closeTab(sessionId: string): Promise<void> {
  const { data } = await supabase
    .from("juliette_sessions")
    .select("metadata")
    .eq("id", sessionId)
    .maybeSingle();
  const meta = ((data as { metadata: Record<string, unknown> | null } | null)?.metadata ??
    {}) as Record<string, unknown>;
  const { error } = await supabase
    .from("juliette_sessions")
    .update({ metadata: { ...meta, tab_closed: true } })
    .eq("id", sessionId);
  if (error) throw new Error(error.message);
}

/** Renomme une conversation (titre editable par l'agent). */
export async function renameThread(sessionId: string, title: string): Promise<void> {
  const clean = title.trim().slice(0, 120);
  const { error } = await supabase
    .from("juliette_sessions")
    .update({ title: clean || null })
    .eq("id", sessionId);
  if (error) throw new Error(error.message);
}

/** Supprime une conversation (CASCADE sur les messages ; mémoire/promesses préservées). */
export async function deleteThread(sessionId: string): Promise<void> {
  const { error } = await supabase.from("juliette_sessions").delete().eq("id", sessionId);
  if (error) throw new Error(error.message);
}

/**
 * Pose un titre auto a partir du 1er message de l'agent si la conversation n'en
 * a pas encore. Best-effort : on n'echoue jamais la conversation pour un titre.
 */
export async function maybeAutoTitleThread(
  sessionId: string,
  firstUserMessage: string,
): Promise<void> {
  const candidate = firstUserMessage.trim().replace(/\s+/g, " ").slice(0, 60);
  if (!candidate) return;
  try {
    const { data } = await supabase
      .from("juliette_sessions")
      .select("title")
      .eq("id", sessionId)
      .maybeSingle();
    const current = (data as { title: string | null } | null)?.title;
    if (current) return; // deja titre
    await supabase.from("juliette_sessions").update({ title: candidate }).eq("id", sessionId);
  } catch {
    // titre best-effort
  }
}
